import { useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";
import MovieGrid from "@/components/feature/MovieGrid";
import MovieFilterPanel from "@/components/feature/MovieFilterPanel";
import SortBar from "@/components/feature/SortBar";
import Pagination from "@/components/feature/Pagination";
import EmptyState from "@/components/feature/EmptyState";
import { usePageMeta } from "@/hooks/usePageMeta";
import { categories, filterOptions } from "@/mocks/site";
import {
  allMovies,
  filterMovies,
  sortMovies,
  paginate,
  pageSize,
  type MovieFilters,
  type SortKey
} from "@/utils/content";

const defaultFilters = (type: string): MovieFilters => ({
  type,
  region: "全部",
  year: "全部",
  rating: "全部"
});

export default function Category() {
  const { type } = useParams();
  const categoryKey = type || "all";
  const meta =
    categories.find((category) => category.key === categoryKey) || categories[0];
  const initialType = meta.type === "all" ? "全部" : meta.type;

  const [filters, setFilters] = useState<MovieFilters>(defaultFilters(initialType));
  const [sortKey, setSortKey] = useState<SortKey>("updated");
  const [page, setPage] = useState(1);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);

  usePageMeta({
    title: `${meta.label} - 秘密影院影视分类`,
    description: `${meta.description} 秘密影院为您整理${meta.label}相关的高分影视资源，支持按类型、地区、年代与评分筛选，查找观看两不误。`,
    keywords: `${meta.label},秘密影院,${meta.label}推荐,影视分类,在线影视`
  });

  useEffect(() => {
    setFilters(defaultFilters(initialType));
    setSortKey("updated");
    setPage(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [categoryKey]);

  const result = useMemo(() => {
    const filtered = filterMovies(allMovies, filters);
    return sortMovies(filtered, sortKey);
  }, [filters, sortKey]);

  const totalPages = Math.max(1, Math.ceil(result.length / pageSize));
  const pageItems = paginate(result, Math.min(page, totalPages));

  const handleFilterChange = (key: keyof MovieFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [key]: value }));
    setPage(1);
  };

  const handleReset = () => {
    setFilters(defaultFilters(initialType));
    setPage(1);
  };

  return (
    <div className="w-full px-4 py-8 md:px-6 md:py-10">
      <nav className="mb-4 flex items-center gap-1 text-sm text-foreground-500" aria-label="面包屑">
        <Link to="/" className="cursor-pointer transition-colors hover:text-accent-400">
          首页
        </Link>
        <span className="flex h-4 w-4 items-center justify-center">
          <i className="ri-arrow-right-s-line" aria-hidden="true" />
        </span>
        <span className="text-foreground-200">{meta.label}</span>
      </nav>

      <header className="mb-8">
        <h1 className="flex items-center gap-3 font-heading text-2xl font-800 text-foreground-50 md:text-3xl">
          <span className="flex h-9 w-9 items-center justify-center rounded-md bg-primary-500 text-accent-400">
            <i className={`${meta.icon} text-lg`} aria-hidden="true" />
          </span>
          {meta.label}
        </h1>
        <p className="mt-3 max-w-3xl text-sm leading-relaxed text-foreground-400">
          {meta.description}
        </p>
      </header>

      <div className="flex gap-8">
        <aside className="hidden w-64 shrink-0 lg:block">
          <div className="sticky top-20 rounded-lg border border-background-800 bg-background-900/70 p-5">
            <h3 className="mb-5 font-heading text-base font-600 text-foreground-50">
              筛选条件
            </h3>
            <MovieFilterPanel
              options={filterOptions}
              value={filters}
              onChange={handleFilterChange}
              onReset={handleReset}
            />
          </div>
        </aside>

        <div className="min-w-0 flex-1">
          <SortBar
            sortKey={sortKey}
            onChange={(key) => {
              setSortKey(key);
              setPage(1);
            }}
            onOpenFilter={() => setMobileFilterOpen(true)}
            resultText={`共找到 ${result.length} 部影视作品`}
          />

          {pageItems.length > 0 ? (
            <>
              <MovieGrid movies={pageItems} />
              <Pagination page={Math.min(page, totalPages)} totalPages={totalPages} onChange={setPage} />
            </>
          ) : (
            <EmptyState
              icon="ri-movie-line"
              title="没有找到符合条件的影视"
              description="当前的筛选条件比较严格，试试放宽条件，或重置筛选查看全部内容。"
              action={
                <button
                  type="button"
                  onClick={handleReset}
                  className="cursor-pointer whitespace-nowrap rounded-md bg-accent-500 px-5 py-2.5 text-sm font-700 text-background-950 transition-colors hover:bg-accent-600"
                >
                  重置筛选条件
                </button>
              }
            />
          )}
        </div>
      </div>

      {mobileFilterOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end bg-background-950/80 backdrop-blur-sm lg:hidden animate-fade-in"
          onClick={() => setMobileFilterOpen(false)}
        >
          <div
            className="max-h-[80vh] w-full overflow-y-auto rounded-t-lg border-t border-background-700 bg-background-900 p-5 animate-scale-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="mb-5 flex items-center justify-between">
              <h3 className="font-heading text-base font-600 text-foreground-50">
                筛选条件
              </h3>
              <button
                type="button"
                onClick={() => setMobileFilterOpen(false)}
                aria-label="关闭筛选"
                className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-md text-foreground-300 hover:text-accent-400"
              >
                <i className="ri-close-line text-lg" aria-hidden="true" />
              </button>
            </div>
            <MovieFilterPanel
              options={filterOptions}
              value={filters}
              onChange={handleFilterChange}
              onReset={handleReset}
            />
            <button
              type="button"
              onClick={() => setMobileFilterOpen(false)}
              className="mt-5 w-full cursor-pointer whitespace-nowrap rounded-md bg-accent-500 py-2.5 text-sm font-700 text-background-950 transition-colors hover:bg-accent-600"
            >
              查看结果（{result.length} 部）
            </button>
          </div>
        </div>
      )}
    </div>
  );
}